import { Alert, Box, Typography } from '@mui/material'
import { NorthEast as NorthEastIcon } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import type { SocialPlatform, SocialPublication } from '@/api/social'
import { TelegramCaption } from '@/components/admin/social/TelegramCaption'
import { postImageUrl } from '@/components/admin/social/socialUi'
import { safeHttpUrl } from '@/utils/url'

interface SocialPostPreviewProps {
  platform: SocialPlatform
  publication: SocialPublication
  /**
   * Показывать, что пост уйдёт без картинки или не отправится. Нужно, пока шаг
   * платформы ещё может уйти: у отправленного или отменённого это уже неправда.
   */
  deliveryWarnings?: boolean
}

/**
 * Рендер поста одной платформы — то, что уйдёт после одобрения. Картинку
 * выбирает postImageUrl так же, как отправка; кнопку Telegram и проверку ссылки
 * VK отдаёт API, предпросмотр их не собирает сам.
 */
export function SocialPostPreview({ platform, publication, deliveryWarnings = true }: SocialPostPreviewProps) {
  const { t } = useTranslation()

  const postImage = postImageUrl(publication)
  // Небезопасный адрес картинку не рисует, но и не значит, что пост уйдёт без неё
  const imageUrl = safeHttpUrl(postImage)
  const vkLinkMissing = publication.vk_news_url_in_text === false

  const image = imageUrl ? (
    <Box
      component="img"
      src={imageUrl}
      alt={t('admin.social.imageAlt')}
      sx={{
        display: 'block',
        width: '100%',
        height: 'auto',
        maxHeight: 360,
        objectFit: 'contain',
        bgcolor: 'action.hover',
        borderRadius: 1,
        mb: 1,
      }}
    />
  ) : postImage === null && deliveryWarnings ? (
    platform === 'telegram' ? (
      // Без картинки API шаг Telegram не отправит
      <Typography variant="caption" color="error.main" sx={{ display: 'block', mb: 1 }}>
        {t('admin.social.telegramNoImage')}
      </Typography>
    ) : vkLinkMissing ? null : (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {t('admin.social.vkNoImage')}
      </Typography>
    )
  ) : null

  const emptyText = (
    <Typography variant="body2" color="text.secondary">
      {t('admin.social.noText')}
    </Typography>
  )

  if (platform === 'telegram') {
    const button = publication.telegram_button
    const buttonUrl = safeHttpUrl(button?.url)

    return (
      <Box sx={{ minWidth: 0 }}>
        {image}
        {publication.text_telegram ? <TelegramCaption html={publication.text_telegram} /> : emptyText}
        {button && buttonUrl && (
          <>
            {/* Плашка, а не ссылка: это предпросмотр inline-кнопки Telegram */}
            <Box
              sx={{
                position: 'relative',
                mt: 1,
                py: 0.75,
                px: 3,
                borderRadius: 1,
                bgcolor: 'action.hover',
                color: 'primary.main',
                textAlign: 'center',
                userSelect: 'none',
                overflowWrap: 'anywhere',
              }}
            >
              <Typography component="span" variant="body2" fontWeight={500}>
                {button.text}
              </Typography>
              <NorthEastIcon aria-hidden sx={{ position: 'absolute', top: 4, right: 4, fontSize: 12 }} />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, overflowWrap: 'anywhere' }}>
              {t('admin.social.telegramButtonUrl', { url: buttonUrl })}
            </Typography>
          </>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      {image}
      {publication.text_vk ? (
        <Typography component="div" variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
          {publication.text_vk}
        </Typography>
      ) : (
        emptyText
      )}
      {vkLinkMissing && deliveryWarnings ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {t('admin.social.vkNewsUrlMissing')}
        </Alert>
      ) : publication.vk_news_url_in_text === true ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {t('admin.social.vkLinkInText')}
        </Typography>
      ) : null}
    </Box>
  )
}
